import * as JStorage from 'rev.sdk.js/Actions/JStorage';

const productCache = new Map();
const productCacheVersions = new Map();
const pendingProducts = new Map();
const productSubscribers = new Map();

function normalizeProductIds(productIds) {
  return [...new Set((productIds || []).filter((id) => !!id))];
}

function getProductCacheVersion(productId) {
  return productCacheVersions.get(productId) || 0;
}

function notifyProductSubscribers(productId) {
  const subscribers = productSubscribers.get(productId);
  if (subscribers) {
    subscribers.forEach((subscriber) =>
      subscriber(productCache.get(productId) || null),
    );
  }
}

function cacheFetchedProduct(product, requestVersions) {
  if (product?.id) {
    const requestVersion = requestVersions.get(product.id);
    if (requestVersion === getProductCacheVersion(product.id)) {
      productCache.set(product.id, product);
      notifyProductSubscribers(product.id);
    }
  }
}

async function fetchMissingProducts(productIds) {
  const requestVersions = new Map(
    productIds.map((id) => [id, getProductCacheVersion(id)]),
  );
  const request = JStorage.fetchAllDocuments('product', {
    _id: {$in: productIds.map((id) => ({$oid: id}))},
  });

  const pendingRequest = request
    .then((products) => {
      products.forEach((product) =>
        cacheFetchedProduct(product, requestVersions),
      );
      productIds.forEach((id) => {
        if (
          requestVersions.get(id) === getProductCacheVersion(id) &&
          !productCache.has(id)
        ) {
          productCache.set(id, null);
          notifyProductSubscribers(id);
        }
      });
    })
    .finally(() => {
      productIds.forEach((id) => pendingProducts.delete(id));
    });

  productIds.forEach((id) => pendingProducts.set(id, pendingRequest));
  await pendingRequest;
}

export async function fetchProductsByIds(productIds, options = {}) {
  const {refresh = false} = options;
  const ids = normalizeProductIds(productIds);
  const missingIds = ids.filter(
    (id) => !pendingProducts.has(id) && (refresh || !productCache.has(id)),
  );

  if (missingIds.length > 0) {
    await fetchMissingProducts(missingIds);
  }

  const pendingRequests = ids
    .map((id) => pendingProducts.get(id))
    .filter((request) => !!request);

  if (pendingRequests.length > 0) {
    await Promise.all(pendingRequests);
  }

  return ids.map((id) => productCache.get(id)).filter((product) => !!product);
}

export async function fetchProductById(productId, options) {
  const products = await fetchProductsByIds([productId], options);
  return products[0] || null;
}

export function getCachedProduct(productId) {
  return productCache.get(productId) || null;
}

export function updateProductCache(product) {
  if (!product?.id) {
    return;
  }

  const cachedProduct = productCache.get(product.id);
  productCacheVersions.set(product.id, getProductCacheVersion(product.id) + 1);
  productCache.set(product.id, {
    ...(cachedProduct || {}),
    ...product,
  });
  notifyProductSubscribers(product.id);
}

export function subscribeProductCache(productId, subscriber) {
  if (!productId || typeof subscriber !== 'function') {
    return () => {};
  }

  const subscribers = productSubscribers.get(productId) || new Set();
  subscribers.add(subscriber);
  productSubscribers.set(productId, subscribers);

  if (productCache.has(productId)) {
    subscriber(productCache.get(productId));
  }

  return () => {
    subscribers.delete(subscriber);
    if (subscribers.size === 0) {
      productSubscribers.delete(productId);
    }
  };
}

export function clearProductCache(productId) {
  if (productId) {
    productCacheVersions.set(productId, getProductCacheVersion(productId) + 1);
    productCache.delete(productId);
    notifyProductSubscribers(productId);
    return;
  }

  const productIds = new Set([
    ...productCache.keys(),
    ...pendingProducts.keys(),
  ]);
  productIds.forEach((id) =>
    productCacheVersions.set(id, getProductCacheVersion(id) + 1),
  );
  productCache.clear();
  productIds.forEach(notifyProductSubscribers);
}
